import {useEffect, useState, type AnchorHTMLAttributes, type MouseEvent} from "react";

export interface LocationSnapshot {
  pathname: string;
  search: string;
  key: string;
}

const navigationBlocks = new Map<symbol, string>();

export function blockNavigation(): () => void {
  const token = Symbol("navigation-block");
  navigationBlocks.set(token, currentRoute());
  return () => navigationBlocks.delete(token);
}

export function useLocationSnapshot(): LocationSnapshot {
  const [location, setLocation] = useState(readLocation);
  useEffect(() => {
    const update = () => {
      const retained = retainedRoute();
      if (retained && currentRoute() !== retained) window.history.pushState({}, "", retained);
      setLocation(readLocation());
    };
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return location;
}

export function navigate(to: string, replace = false): void {
  if (retainedRoute()) return;
  if (replace) window.history.replaceState({}, "", to);
  else window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({top: 0, behavior: "auto"});
}

export function AppLink({href = "", onClick, ...props}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !href.startsWith("/")) return;
    event.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={handleClick} {...props} />;
}

function readLocation(): LocationSnapshot {
  return {pathname: window.location.pathname, search: window.location.search, key: `${window.location.pathname}${window.location.search}`};
}

function currentRoute(): string { return `${window.location.pathname}${window.location.search}${window.location.hash}`; }
function retainedRoute(): string | undefined { return [...navigationBlocks.values()].at(-1); }
